import React from 'react';
import type { GasSupplyZoneCommitment } from '../../lib/api';

interface Props {
  zone: GasSupplyZoneCommitment;
}

const MW_PER_MMCF = 5.07;

const formatGw = (bcfd: number | null): string | null => {
  if (bcfd == null) return null;
  const gw = bcfd * MW_PER_MMCF;
  return gw >= 1 ? `${gw.toFixed(1)} GW` : `${Math.round(gw * 1000)} MW`;
};

const formatAcres = (n: number | null): string | null => {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M ac`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k ac`;
  return `${n.toLocaleString()} ac`;
};

export const OperatorZoneCalloutV2: React.FC<Props> = ({ zone }) => {
  const gwLabel = formatGw(zone.peakBcfd);
  const acLabel = formatAcres(zone.dedicatedAcres);
  const inventoryLabel = zone.inventoryYears ? `${zone.inventoryYears}-yr inventory` : null;

  // Phase 1 ramp annotation (Revenant-specific for now). Surfaces the day-1
  // deliverability number next to the long-term peak so an IC reader
  // doesn't mistake the 6.1 GW peak for what's accessible at financial close.
  const phase1 = zone.slug === 'revenant-east-tx' ? '~250 MW deliverable by Jun 2027' : null;
  const peakLabel = gwLabel
    ? `${gwLabel} peak supply${zone.peakYear ? ` by ${zone.peakYear}` : ''}${phase1 ? ` (${phase1})` : ''}`
    : null;
  const facts = [zone.assetName, peakLabel, acLabel ? `${acLabel} of operator-controlled acreage` : null, inventoryLabel].filter(Boolean) as string[];

  return (
    <div className="v2-zone-callout">
      <div className="v2-zone-callout-text">
        <div className="v2-zone-eyebrow">
          Inside the {zone.operatorName} supply zone
        </div>
        <div className="v2-zone-body">
          {facts.join(' · ')}.
          <span className="v2-zone-note">
            {' '}{zone.operatorName} has indicated they will build operator gathering to qualified BTM buyers — pipeline tap candidates are redundancy, not the firm path.
          </span>
        </div>
      </div>
      <a
        className="v2-zone-link"
        href={zone.zoneUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        Zone deck <span className="v2-arrow">&rarr;</span>
      </a>
    </div>
  );
};
